import type { ViewCachePolicy, ViewRenderKind } from '@hibro/shared';
import type { DockviewPanelRenderer } from 'dockview-react';

/**
 * Pure MRU keep-alive logic for the editor area (架构方案 §5.7 视图缓存). The
 * `EditorSlot` keeps a recency-ordered list of mounted view *instance ids*
 * (`viewId` or `viewId::instanceKey`, 见 editor-tabs.ts): the head is the
 * active instance; the rest stay mounted but hidden so switching back restores
 * their React tree / iframe DOM instead of remounting. Eviction = dropping an
 * id from the list — React unmount then runs the existing
 * `deactivateView`/`deactivateForView` teardown chain (§5.4) untouched.
 *
 * Kept pure (no React) so the ordering/eviction rules are unit-testable,
 * mirroring `editor-tabs.ts`.
 */

/** Max hidden (non-active) kept-alive instances before LRU eviction. */
export const EDITOR_CACHE_CAPACITY = 3;

/**
 * Resolve a view's effective cache policy (架构方案 §5.7 视图缓存). An explicit
 * manifest `cache` wins for the inline render kinds; omitted, both kinds that
 * actually hold inline content (`inline` / `iframe`) default to `keep-alive`.
 *
 * The default sides with the user, not with the cheaper remount: losing scroll
 * position, a half-typed message or a scrolled-back conversation on every tab
 * switch is what people notice, and a module that genuinely should not be
 * preserved says so with `cache: 'none'`. The non-inline kinds
 * (`external`/`remote`) have no cacheable inline content, so they are never kept
 * alive regardless of declaration.
 */
export function resolveViewCachePolicy(view: {
  kind?: ViewRenderKind;
  cache?: ViewCachePolicy;
}): ViewCachePolicy {
  if (view.kind !== 'inline' && view.kind !== 'iframe') {
    return 'none';
  }
  return view.cache ?? 'keep-alive';
}

/**
 * Map a cache policy onto the dockview renderer of a panel hosting that view
 * (架构方案 §5.7 视图缓存), so «is this view worth keeping alive» is answered
 * once for both presentations — the MRU pane inside `EditorSlot` and the native
 * instance tabs beside it.
 *
 * dockview's default `onlyWhenVisible` takes the panel's content element out of
 * the document the moment another tab in the group activates. The React tree
 * survives that (dockview-react keeps the portal until the panel is *removed*),
 * but DOM-level state does not: an `<iframe>` loses its browsing context and
 * reloads on re-insertion, scroll offsets reset, and measured bounds go stale.
 * `always` renders the panel into dockview's overlay container and only hides
 * it, which is what keep-alive means at the DOM level.
 */
export function resolveTabRenderer(
  policy: ViewCachePolicy,
): DockviewPanelRenderer {
  return policy === 'keep-alive' ? 'always' : 'onlyWhenVisible';
}

/**
 * Compute the next MRU-ordered list of mounted editor instances from the
 * current list and the active instance id. Rules:
 * - the active id (if any) moves to the head (touch);
 * - hidden entries (everything but the active) are kept only while
 *   `isCacheable` holds (view still exists, keep-alive policy, not detached);
 * - at most `capacity` hidden entries survive, dropping the least recently
 *   used first (list order encodes recency: earlier = more recent).
 * Pure and idempotent, so it is safe to run during render (derived state).
 */
export function planMountedInstances(
  mounted: readonly string[],
  activeId: string | null,
  isCacheable: (instanceId: string) => boolean,
  capacity: number = EDITOR_CACHE_CAPACITY,
): readonly string[] {
  const hidden = mounted
    .filter((id) => id !== activeId && isCacheable(id))
    .slice(0, capacity);
  return activeId ? [activeId, ...hidden] : hidden;
}

/** Drop every instance matching `predicate` (event-driven eviction, §5.7). */
export function removeInstances(
  mounted: readonly string[],
  predicate: (instanceId: string) => boolean,
): readonly string[] {
  return mounted.filter((id) => !predicate(id));
}

/**
 * Stable *DOM render* order for the mounted panes, decoupled from the MRU
 * recency order. The panes are absolutely-positioned overlays, so DOM order is
 * visually irrelevant — but it must never change for surviving entries: if the
 * keyed list reorders, React moves the pane's DOM node (`insertBefore`), and a
 * moved `<iframe>` reloads its document by spec — which would defeat the
 * keep-alive cache on every switch. Sorting by id means two surviving entries
 * never swap relative order; membership changes only insert/remove nodes.
 */
export function renderOrder(mounted: readonly string[]): readonly string[] {
  return [...mounted].sort();
}

/** Shallow list equality, to skip no-op state updates during render. */
export function sameInstanceList(
  a: readonly string[],
  b: readonly string[],
): boolean {
  return a.length === b.length && a.every((id, index) => id === b[index]);
}
